import React, { useState, useEffect } from 'react';
import { format, formatDistanceToNow, isBefore, parseISO } from 'date-fns';
import { Clock, ChevronLeft, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';

interface ScheduleTimelineProps {
  booking: {
    drop_off_date?: string;
    drop_off_time?: string;
    start_date?: string;
    start_time?: string;
    end_date?: string;
    end_time?: string;
    mobile_booking?: boolean;
    status: string;
  };
  isEditing?: boolean;
  onEdit?: () => void;
  canEdit?: boolean;
  isMobile?: boolean;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
  onRescheduleMobileBooking?: () => void;
}

interface TimePoint {
  type: 'drop-off' | 'start' | 'end';
  date?: string;
  time?: string;
  label: string;
  isComplete?: boolean;
  isCurrent?: boolean;
}

export default function ScheduleTimeline({
  booking,
  isEditing = false,
  onEdit,
  canEdit = false,
  isMobile = false,
  onStatusChange,
  readOnly = false,
  onRescheduleMobileBooking
}: ScheduleTimelineProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Status management
  const statuses = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  const currentStatusIndex = statuses.findIndex(s => s.value === booking.status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-500';
      case 'scheduled':
        return 'text-blue-600';
      case 'in_progress':
        return 'text-yellow-600';
      case 'completed':
        return 'text-green-600';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'in_progress':
        return <Clock className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const handleStatusUp = () => {
    if (canStatusGoUp && onStatusChange) {
      onStatusChange(statuses[currentStatusIndex - 1].value);
    }
  };

  const handleStatusDown = () => {
    if (canStatusGoDown && onStatusChange) {
      onStatusChange(statuses[currentStatusIndex + 1].value);
    }
  };
  
  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Format relative time
  const getRelativeTime = (date?: string, time?: string) => {
    if (!date) return null;
    
    try {
      const dateTime = parseISO(`${date}T${time || '00:00'}`);
      const now = currentTime;
      
      if (isBefore(dateTime, now)) {
        return `${formatDistanceToNow(dateTime)} ago`;
      } else {
        return `in ${formatDistanceToNow(dateTime)}`;
      }
    } catch {
      return null;
    }
  };

  // Determine timeline points
  const getTimelinePoints = (): TimePoint[] => {
    const points: TimePoint[] = [];
    
    if (!booking.mobile_booking) {
      points.push({
        type: 'drop-off',
        date: booking.drop_off_date,
        time: booking.drop_off_time,
        label: 'Drop-off',
        isComplete: booking.status !== 'created' && booking.status !== 'scheduled'
      });
    }
    
    points.push({
      type: 'start',
      date: booking.start_date,
      time: booking.start_time,
      label: booking.mobile_booking ? 'Scheduled' : 'Start',
      isComplete: booking.status === 'in_progress' || booking.status === 'completed',
      isCurrent: booking.status === 'in_progress'
    });
    
    points.push({
      type: 'end',
      date: booking.end_date,
      time: booking.end_time,
      label: booking.mobile_booking ? 'Complete By' : 'Pick-up',
      isComplete: booking.status === 'completed'
    });
    
    return points;
  };

  const timelinePoints = getTimelinePoints();

  return (
    <div className="relative">
      
      {/* Timeline */}
      <div className={`relative ${isMobile ? 'overflow-x-auto -mx-2 px-2' : ''}`}>
        {/* Timeline Points */}
        <div className={`relative flex justify-between items-start ${isMobile ? 'min-w-[280px]' : ''}`}>
          {timelinePoints.map((point, index) => {
            const relativeTime = getRelativeTime(point.date, point.time);
            const isActive = point.isCurrent || (booking.status === 'in_progress' && point.type === 'start');
            
            return (
              <div 
                key={point.type}
                className={`flex-1 text-center relative group ${
                  (canEdit && onEdit && !isEditing) || (booking.mobile_booking && onRescheduleMobileBooking) ? 'cursor-pointer' : ''
                }`}
                onClick={() => {
                  if (booking.mobile_booking && onRescheduleMobileBooking) {
                    onRescheduleMobileBooking();
                  } else if (canEdit && onEdit && !isEditing) {
                    onEdit();
                  }
                }}
              >
                {/* Content */}
                <div className={`pt-2 ${
                  (canEdit && onEdit && !isEditing) || (booking.mobile_booking && onRescheduleMobileBooking) ? 'hover:opacity-70 transition-opacity' : ''
                }`}>
                  {/* Label */}
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <span className={`
                      text-xs font-semibold uppercase tracking-wider
                      ${point.isComplete 
                        ? 'text-green-600' 
                        : isActive
                          ? 'text-indigo-600'
                          : 'text-gray-500'
                      }
                    `}>
                      {point.label}
                    </span>
                  </div>

                  {/* Date & Time */}
                  {point.date && point.time ? (
                    <div className="space-y-1">
                      <div className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-900`}>
                        {format(parseISO(`${point.date}T${point.time}`), isMobile ? 'MMM d' : 'MMM d, yyyy')}
                      </div>
                      <div className="flex items-center justify-center space-x-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className={`${isMobile ? 'text-[11px]' : 'text-xs'} text-gray-600`}>
                          {format(parseISO(`2000-01-01T${point.time}`), 'h:mm a')}
                        </span>
                      </div>
                      {relativeTime && !isMobile && (
                        <div className="text-[11px] text-gray-500 font-medium">
                          {relativeTime}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>Not scheduled</div>
                  )}
                </div>


              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}